// The dashboard API client: same-origin JSON requests (the session cookie goes along), RFC 9457 problems
// as ApiError, Idempotency-Key on creating POSTs, and a single retry after the user re-authenticates
// when the API asks for it (403 reauthentication_required).

import type { Problem } from '@cck/schema';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly problem: Problem | null;

  constructor(status: number, problem: Problem | null) {
    super(problem?.detail ?? problem?.title ?? `HTTP ${status}`);
    this.status = status;
    this.code = problem?.code ?? 'http_error';
    this.problem = problem;
  }

  /** Field errors by JSON Pointer ("/email") or query path ("query.to"). */
  fieldErrors(): Record<string, string> {
    return Object.fromEntries((this.problem?.errors ?? []).map((e) => [e.path, e.message]));
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /** Sends an Idempotency-Key, so that a retried request cannot act twice. */
  idempotent?: boolean;
  /** Returns the raw Response (files, CSV). */
  raw?: boolean;
  signal?: AbortSignal;
}

type ReauthHandler = () => Promise<boolean>;
let reauthHandler: ReauthHandler | null = null;
let unauthorizedHandler: (() => void) | null = null;

/** The re-authentication dialog registers itself here (see reauth.tsx). */
export function onReauthRequired(h: ReauthHandler | null): void {
  reauthHandler = h;
}

/** Called when the session has ended (401) on a request that needed one. */
export function onUnauthorized(h: (() => void) | null): void {
  unauthorizedHandler = h;
}

export const API_BASE = '/api/v1';

async function send(path: string, o: RequestOptions, key: string | undefined): Promise<Response> {
  const headers: Record<string, string> = { accept: 'application/json', ...o.headers };
  if (o.body !== undefined) headers['content-type'] = 'application/json';
  if (key) headers['idempotency-key'] = key;
  return fetch(`${API_BASE}${path}`, {
    method: o.method ?? 'GET', headers, credentials: 'same-origin',
    ...(o.body !== undefined ? { body: JSON.stringify(o.body) } : {}), ...(o.signal ? { signal: o.signal } : {}),
  });
}

async function problemOf(res: Response): Promise<Problem | null> {
  try {
    return (await res.json()) as Problem;
  } catch {
    return null;
  }
}

export async function api<T = unknown>(path: string, o: RequestOptions = {}): Promise<T> {
  const key = o.idempotent ? crypto.randomUUID() : undefined;
  let res = await send(path, o, key);
  if (res.status === 403) {
    const problem = await problemOf(res.clone());
    if (problem?.code === 'reauthentication_required' && reauthHandler && (await reauthHandler())) {
      res = await send(path, o, key);
    }
  }
  if (!res.ok) {
    const problem = await problemOf(res);
    if (res.status === 401 && !path.startsWith('/auth/') && path !== '/me') unauthorizedHandler?.();
    throw new ApiError(res.status, problem);
  }
  if (o.raw) return res as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Uploads a file (photo, certificate) and returns its upload ID. */
export type UploadPurpose = 'corrective_action_photo' | 'calibration_certificate' | 'maintenance_invoice' | 'maintenance_photo'
  | 'cooling_photo' | 'delivery_note' | 'cleaning_photo';

export async function uploadFile(orgId: string, file: Blob, purpose: UploadPurpose): Promise<string> {
  const ticket = await api<{ upload_id: string; url: string; headers: Record<string, string> }>(`/orgs/${orgId}/uploads`,
    { method: 'POST', body: { purpose, content_type: file.type, size_bytes: file.size } });
  const res = await fetch(ticket.url, { method: 'PUT', headers: ticket.headers, body: file, credentials: 'same-origin' });
  if (!res.ok) throw new ApiError(res.status, await problemOf(res));
  return ticket.upload_id;
}

/** Saves data as a file. The link is attached while clicked: detached, some browsers ignore its name. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.hidden = true;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Downloads a response body as a file. */
export async function saveResponse(res: Response, filename: string): Promise<void> {
  saveBlob(await res.blob(), filename);
}
